<template>
  <v-dialog v-model="dialog" persistent max-width="520px">
    <v-card class="person-dialog-card">
      <div class="dialog-header">
        <div>
          <div class="dialog-title">Edit Delivery Person</div>
          <div class="dialog-subtitle">
            Update warehouse access and delivery-person information.
          </div>
        </div>
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4">
        <div class="field-label">Name</div>
        <v-text-field
          ref="name"
          v-model.trim="person.name"
          label="Name"
          outlined
          dense
          :error-messages="nameErrors"
        />

        <div class="field-label">Description</div>
        <v-textarea
          v-model.trim="person.note"
          label="Note (optional)"
          outlined
          dense
          rows="2"
        />

        <v-switch
          v-model="person.active"
          label="Allow this person to receive deliveries"
          color="success"
          inset
        />

        <div class="access-box">
          <div class="access-heading">Warehouse access</div>
          <div class="access-help">
            Generating a new code will immediately make the old link invalid
            after saving.
          </div>
          <div class="access-row mt-3">
            <v-text-field
              v-model="person.accessCode"
              label="Access code"
              outlined
              dense
              readonly
              hide-details
            />
            <v-btn outlined color="primary" @click="regenerate">
              <v-icon left>mdi-refresh</v-icon>
              New Code
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="dialog-actions">
        <v-spacer />
        <v-btn text color="primary" @click="close">Close</v-btn>
        <v-btn
          color="primary"
          depressed
          rounded
          dark
          :loading="loading"
          @click="submit"
        >
          Update Person
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from "lodash";
import service from "../service";
const { validationMixin } = require("vuelidate");
const { required, minLength } = require("vuelidate/lib/validators");

export default {
  name: "EditInboundDeliveryPerson",
  mixins: [validationMixin],
  props: {
    editDialog: Boolean,
    editPerson: Object,
  },
  data() {
    return {
      dialog: false,
      loading: false,
      person: {},
    };
  },
  validations: {
    person: {
      name: { required, minLength: minLength(2) },
      accessCode: { required },
    },
  },
  computed: {
    nameErrors() {
      const errors = [];
      if (!this.$v.person.name.$dirty) return errors;
      if (!this.$v.person.name.required) errors.push("Name is required.");
      if (!this.$v.person.name.minLength)
        errors.push("Name must contain at least 2 characters.");
      return errors;
    },
  },
  watch: {
    editDialog(value) {
      this.dialog = value;
      if (value && this.editPerson) {
        this.person = _.cloneDeep(this.editPerson);
        this.$v.$reset();
        this.$nextTick(() => this.$refs.name && this.$refs.name.focus());
      }
    },
  },
  methods: {
    regenerate() {
      this.person.accessCode = service.generateAccessCode();
    },
    close() {
      if (this.loading) return;
      this.dialog = false;
      this.$emit("close");
    },
    async submit() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.loading = true;
      try {
        await service.update(this.person.id, {
          name: this.person.name,
          note: this.person.note,
          active: this.person.active,
          accessCode: this.person.accessCode,
        });
        this.loading = false;
        this.$emit("submit");
        this.close();
        this.$swal("Updated", "Delivery person has been updated.", "success");
      } catch (error) {
        const message =
          (error.data && error.data.message) || "Unable to update delivery person.";
        this.$swal("Delivery Person", message, "error");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.person-dialog-card {
  border-radius: 20px;
  overflow: hidden;
}
.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.dialog-title {
  color: #0f172a;
  font-size: 20px;
  font-weight: 800;
}
.dialog-subtitle {
  color: #64748b;
  font-size: 13px;
  margin-top: 2px;
}
.field-label {
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
}
.dialog-actions {
  padding: 12px 16px;
}
.access-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
}
.access-heading {
  color: #0f172a;
  font-weight: 800;
}
.access-help {
  color: #64748b;
  font-size: 12px;
  margin-top: 3px;
}
.access-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
@media (max-width: 600px) {
  .access-row {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
